import localforage from 'localforage'

const storage = localforage.createInstance({
    name: 'admin-frontent',
    storeName: 'api_cache'
})

const inflight = {}

function clone(value) {
    if (value === undefined || value === null) {
        return value
    }
    return JSON.parse(JSON.stringify(value))
}

async function getEntry(key) {
    return storage.getItem(key)
}

async function setEntry(key, value) {
    const now = new Date().toISOString()
    const current = await getEntry(key)
    const entry = {
        value: clone(value),
        createdAt: current?.createdAt || now,
        updatedAt: now
    }
    await storage.setItem(key, entry)
    return clone(entry.value)
}

async function remember(key, loader, options = {}) {
    const {
        force = false,
        maxAge = null,
        revalidate = false,
        onUpdate = null,
        onRevalidateStart = null,
        onRevalidateDone = null
    } = options

    if (!force) {
        const cached = await getEntry(key)
        if (cached) {
            const expiresAt = maxAge ? new Date(cached.updatedAt).getTime() + maxAge : null
            if (!expiresAt || expiresAt > Date.now()) {
                if (revalidate) {
                    if (onRevalidateStart) {
                        onRevalidateStart()
                    }
                    if (inflight[key]) {
                        if (onUpdate) {
                            inflight[key].then(onUpdate).catch(error => console.error('Cache revalidation failed', error))
                        }
                        if (onRevalidateDone) {
                            inflight[key].finally(onRevalidateDone).catch(() => {})
                        }
                    } else {
                        inflight[key] = Promise.resolve(loader())
                            .then(value => setEntry(key, value))
                            .then(value => {
                                if (onUpdate) {
                                    onUpdate(value)
                                }
                                return value
                            })
                            .catch(error => {
                                console.error('Cache revalidation failed', error)
                                return clone(cached.value)
                            })
                            .finally(() => {
                                delete inflight[key]
                                if (onRevalidateDone) {
                                    onRevalidateDone()
                                }
                            })
                    }
                }
                return clone(cached.value)
            }
        }
    }

    if (inflight[key]) {
        return inflight[key]
    }

    inflight[key] = Promise.resolve(loader())
        .then(value => setEntry(key, value))
        .finally(() => {
            delete inflight[key]
        })

    return inflight[key]
}

async function updateArrayItem(key, itemId, updater, options = {}) {
    const { insert = false } = options
    const entry = await getEntry(key)
    if (!entry || !Array.isArray(entry.value)) {
        return null
    }

    let found = false
    const next = entry.value.map(item => {
        if (String(item?.id) !== String(itemId)) {
            return item
        }
        found = true
        return typeof updater === 'function' ? updater(clone(item)) : updater
    })

    if (!found && insert) {
        next.unshift(typeof updater === 'function' ? updater(null) : updater)
    }

    return setEntry(key, next)
}

async function removeArrayItem(key, itemId) {
    const entry = await getEntry(key)
    if (!entry || !Array.isArray(entry.value)) {
        return null
    }

    return setEntry(key, entry.value.filter(item => String(item?.id) !== String(itemId)))
}

async function keys() {
    return storage.keys()
}

export default {
    remember,
    set: setEntry,
    updateArrayItem,
    removeArrayItem,
    keys
}
